import { FieldError, useFormContext } from 'react-hook-form';

import { useNationalities } from '@/components/shared/components/NewContextForms/AutocompleteNationalityFormField/hooks/useNationalities';
import { geocoderToAddressFieldsMapper } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/GoogleMapsAutocomplete/mappers/geocoderToAddressFieldsMapper';
import { Geocode } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/GoogleMapsAutocomplete/types';
import { ADDRESS_BLANK_VALUES } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/constants/AddressBlankValues';
import { DECLARED_FIELDS_NAMES } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/constants/declaredFieldsNames';
import { useAddressContext } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/contexts/AddressContext';
import { AddressSchema } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/schemas/AddressSchema';
import { getFormFieldData } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/services/getFormFieldData';
import { AddressMode } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/types/AddressMode';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { getFormattedAddress } from '@/utils/getFormattedAddress';
import { parseZodSchema } from '@/utils/parseZodSchema';

export const useAddressFormFields = () => {
  const { getTextIdTranslation: t } = useTranslation();
  const { nationalities } = useNationalities();
  const { formState, setValue, reset, resetField, getValues, getFieldState, watch, trigger } = useFormContext();
  const {
    setMode,
    fieldNamesPrefix,
    temporaryDialogData,
    setTemporaryDialogData,
    temporaryCardData,
    setTemporaryCardData,
    setFocusAutocomplete,
  } = useAddressContext();

  const initializeDialog = (): void => {
    saveTemporaryDialogData();
    saveTemporaryCardData();
    reset({ ...getValues() }); // remove isDirty from formState

    const values = { ...getValues(fieldNamesPrefix) };

    if (parseZodSchema(values, AddressSchema(t))) {
      setMode(AddressMode.CONFIRM);
    } else if (!values.address || !values.streetName) {
      setMode(AddressMode.AUTOCOMPLETE);
      setTimeout(() => {
        setFocusAutocomplete(true);
      }, 500);
    }
  };

  const saveTemporaryDialogData = (): void => {
    setTemporaryDialogData({ ...getValues(fieldNamesPrefix) });
  };

  const saveTemporaryCardData = (): void => {
    setTemporaryCardData({ ...getValues(fieldNamesPrefix) });
  };

  const restoreTemporaryDialogData = (): void => {
    resetField(fieldNamesPrefix, {
      defaultValue: { ...temporaryDialogData },
    });
  };

  const restoreTemporaryCardData = (): void => {
    resetField(fieldNamesPrefix, {
      defaultValue: {
        ...temporaryCardData,
        floorNumber: getValues(`${fieldNamesPrefix}.floorNumber`),
        doorNumber: getValues(`${fieldNamesPrefix}.doorNumber`),
        building: getValues(`${fieldNamesPrefix}.building`),
      },
      keepDirty: true,
    });
  };

  const hasTemporaryCardData: boolean = !!temporaryCardData?.address && !!temporaryCardData?.streetName;

  const cardHasChanges: boolean = DECLARED_FIELDS_NAMES.some(
    (fieldName) => watch(`${fieldNamesPrefix}.${fieldName}`) !== temporaryCardData?.[fieldName],
  );

  const dialogHasChanges: boolean = ['address', ...DECLARED_FIELDS_NAMES].some(
    (fieldName) => watch(`${fieldNamesPrefix}.${fieldName}`) !== temporaryDialogData?.[fieldName],
  );

  const emptyFields = (): void => {
    resetField(fieldNamesPrefix, {
      defaultValue: {
        ...ADDRESS_BLANK_VALUES,
        address: '',
        floorNumber: getValues(`${fieldNamesPrefix}.floorNumber`),
        doorNumber: getValues(`${fieldNamesPrefix}.doorNumber`),
        building: getValues(`${fieldNamesPrefix}.building`),
      },
    });
  };

  const updateCountryLongName = (countryValue: string): void => {
    const countryLongName = nationalities.find((country) => country?.alpha2 === countryValue)?.display;
    if (countryLongName) {
      setValue(`${fieldNamesPrefix}.countryLongName`, countryLongName);
    }
  };

  const updateFieldsFromGeocode = (geocode: Geocode): void => {
    const geocodeAddress = geocoderToAddressFieldsMapper(geocode);
    setValue(
      fieldNamesPrefix,
      {
        address: getFormattedAddress(geocodeAddress),
        ...geocodeAddress,
        floorNumber: getValues(`${fieldNamesPrefix}.floorNumber`),
        doorNumber: getValues(`${fieldNamesPrefix}.doorNumber`),
        building: getValues(`${fieldNamesPrefix}.building`),
      },
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const updateFormattedAddressField = (): void => {
    const values = { ...getValues(fieldNamesPrefix) };
    setValue(`${fieldNamesPrefix}.address`, getFormattedAddress(values));
  };

  const getValidation = async (): Promise<boolean> => {
    await trigger(fieldNamesPrefix);
    return !(await getFormFieldData(fieldNamesPrefix, formState.errors)) && !getFieldState(fieldNamesPrefix).error;
  };

  const areFieldsValid: boolean = !getFieldState(fieldNamesPrefix).error;

  const forceAutocompleteError = (): FieldError | undefined => {
    const values = { ...getValues(fieldNamesPrefix) };
    if (
      formState.isDirty &&
      !values.streetName &&
      !values.postalCode &&
      !values.city &&
      !values.province &&
      !values.country
    )
      return {
        type: 'required',
        message: t('AUTOCOMPLETE_ADDRESS'),
      };
  };

  // console.log(formState.errors, getValues(), { isDirty: formState.isDirty, areFieldsValid, dialogHasChanges });

  return {
    initializeDialog,
    saveTemporaryDialogData,
    saveTemporaryCardData,
    restoreTemporaryDialogData,
    restoreTemporaryCardData,
    hasTemporaryCardData,
    cardHasChanges,
    dialogHasChanges,
    emptyFields,
    updateCountryLongName,
    updateFieldsFromGeocode,
    updateFormattedAddressField,
    getValidation,
    areFieldsValid,
    forceAutocompleteError,
  };
};
